import { Component, HostListener } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ProductService } from '../products/services/product.service';
import { Product } from '../../model/product';
import { Category } from '../../model/category';
import { environment } from 'src/environments/environment';
import { HomeService } from '../home/services/home.service';
import { forkJoin, of, switchMap } from 'rxjs';
import { CurrencySelectionService } from 'src/app/services/currency/currency-selection.service';
import { ProductDetailsService } from './services/product-details.service';
import { AppModeService } from 'src/app/services/app-mode.service';
import { CartItem, CartService } from 'src/app/services/cart/cart.service';

interface SelectedSize {
  size: string;
  quantity: number;
}

interface ColorSizeSelection {
  [color: string]: SelectedSize[];
}

@Component({
  selector: 'app-product-details',
  templateUrl: './product-details.component.html',
  styleUrls: ['./product-details.component.css'],
})
export class ProductDetailsComponent {
  isScrolled = false;

  @HostListener('window:scroll', [])
  onWindowScroll() {
    this.isScrolled = window.scrollY > 50;
  }
  selectedCurrency: string = 'EGP';
  convertedPrice: number = 0;
  colorSizeSelections: ColorSizeSelection = {};
  sizes: string[] = [];
  selectedColor: string = '';
  selectedImage: any;
  colors: string[] = [];
  productItem: any = null;
  products: any[] = [];
  isWishlisted: boolean = false;
  private readonly wishlistKey = 'wembely_wishlist_ids';

  constructor(
    private route: ActivatedRoute,
    private productService: ProductService,
    private homeService: HomeService,
    private router: Router,
    private cartService: CartService,
    private currencyService: CurrencySelectionService,
    private productDetailsService: ProductDetailsService,
    public readonly appModeService: AppModeService,
  ) {}

  ngOnInit(): void {
    const id = this.route.snapshot.paramMap.get('id');
    this.currencyService.selectedCurrency$.subscribe((currency) => {
      this.selectedCurrency = currency;
      if (this.productItem) {
        this.updateConvertedPrice();
      }
    });

    if (id) {
      this.isWishlisted = this.readWishlistIds().includes(id);
      this.productService
        .getProductById(id)
        .pipe(
          switchMap((product: Product) =>
            forkJoin({
              product: of(product),
              category: this.homeService.getCategoryByName(
                product.category.name,
              ),
            }),
          ),
        )
        .subscribe(({ product, category }) => {
          this.sizes = Array.isArray(category?.sizes)
            ? this.sortSizes(category.sizes)
            : [];
          this.productItem = this.mapResToProduct(product);
          this.extractColorsAndSetDefaults();
          this.initializeColorSelections();
          this.updateConvertedPrice();
          this.loadRelatedProducts(
            this.productItem.category,
            id,
            this.selectedColor,
          );
        });
    }
  }

  private extractColorsAndSetDefaults(): void {
    this.colors = Array.from(
      new Set<string>(
        this.productItem.images.map((img: any) => String(img.color)),
      ),
    );
    this.selectedColor = this.productItem.images[0]?.color || '';
    this.selectedImage = this.productItem.images[0] || null;
  }

  private initializeColorSelections(): void {
    this.colors.forEach((color) => {
      if (!this.colorSizeSelections[color]) {
        this.colorSizeSelections[color] = [];
      }
    });
  }

  get selectedSizes(): SelectedSize[] {
    return this.colorSizeSelections[this.selectedColor] || [];
  }

  toggleSizeSelection(size: string): void {
    if (!this.colorSizeSelections[this.selectedColor]) {
      this.colorSizeSelections[this.selectedColor] = [];
    }

    const currentSelections = this.colorSizeSelections[this.selectedColor];
    const index = currentSelections.findIndex((s) => s.size === size);

    if (index >= 0) {
      currentSelections.splice(index, 1);
    } else {
      currentSelections.push({ size, quantity: 1 });
    }
  }

  removeSelectedSize(size: string): void {
    if (this.colorSizeSelections[this.selectedColor]) {
      this.colorSizeSelections[this.selectedColor] = this.colorSizeSelections[
        this.selectedColor
      ].filter((s) => s.size !== size);
    }
  }

  adjustQuantity(sizeItem: SelectedSize, change: number): void {
    const newQuantity = sizeItem.quantity + change;
    if (newQuantity > 0) {
      sizeItem.quantity = newQuantity;
    }
  }

  isSizeSelected(size: string): boolean {
    return this.selectedSizes.some((s) => s.size === size);
  }

  getTotalQuantity(): number {
    return this.selectedSizes.reduce((sum, item) => sum + item.quantity, 0);
  }

  getTotalPrice(): number {
    return this.getTotalQuantity() * this.convertedPrice;
  }

  getGrandTotal(): { quantity: number; price: number } {
    let totalQuantity = 0;
    let totalPrice = 0;

    Object.values(this.colorSizeSelections).forEach((selections) => {
      const colorQuantity = selections.reduce(
        (sum, item) => sum + item.quantity,
        0,
      );
      totalQuantity += colorQuantity;
      totalPrice += colorQuantity * this.convertedPrice;
    });

    return { quantity: totalQuantity, price: totalPrice };
  }

  updateConvertedPrice(): void {
    if (!this.productItem?.price) return;
    this.currencyService
      .convertPrice(this.productItem.price)
      .subscribe((converted) => (this.convertedPrice = converted));
  }

  getCurrencySymbol(): string {
    return this.currencyService.getSelectedCurrencySymbol();
  }

  sortSizes(sizes: string[]): string[] {
    const trimmedSizes = sizes
      .map((size) => (size || '').trim())
      .filter((size) => size);
    const years = trimmedSizes
      .filter((size) => size.toLowerCase().includes('year'))
      .sort((a, b) => parseInt(a) - parseInt(b));

    const sizeOrder = ['XS', 'S', 'M', 'L', 'XL', 'XXL', '3XL', '4XL'];
    const regularSizes = trimmedSizes
      .filter((size) => !size.toLowerCase().includes('year'))
      .sort((a, b) => {
        const labelA = a.split('=')[0].trim().toUpperCase();
        const labelB = b.split('=')[0].trim().toUpperCase();
        return sizeOrder.indexOf(labelA) - sizeOrder.indexOf(labelB);
      });

    return [...years, ...regularSizes];
  }

  loadRelatedProducts(categoryName: string, excludeId: string, color: string) {
    this.productService
      .getRelatedProducts(categoryName, excludeId, color)
      .subscribe((res: Product[]) => {
        this.products = res.map((product: Product) => {
          const mainProductColor = product.productColors.find(
            (pc) => pc.color.toLowerCase() === color.toLowerCase(),
          );
          const media = mainProductColor?.productMedia[0];

          return {
            id: product.id,
            imagePath: media
              ? environment.apiUrl + media.mediaUrl
              : 'assets/img/default-product.png',
            name: product.name,
            rating: 3,
            price: product.price,
            isNew: false,
          };
        });
      });
  }

  selectImage(image: any) {
    this.selectedImage = image;
    this.selectedColor = image.color;
  }

  selectColor(color: string) {
    this.selectedColor = color;
    const matchedImage = this.productItem.images.find(
      (img: any) => img.color === color,
    );
    if (matchedImage) {
      this.selectedImage = matchedImage;
    }
    this.loadRelatedProducts(
      this.productItem.category,
      this.route.snapshot.paramMap.get('id')!,
      color,
    );
  }

  mapResToProduct(product: Product): Object {
    return {
      images: product.productColors.flatMap((color) =>
        color.productMedia.map((media) => ({
          imageUrl: environment.apiUrl + media.mediaUrl,
          color: color.color,
        })),
      ),
      name: product.name,
      category: product.category.name,
      rating: '5',
      price: Number(product.price),
      oldPrice: 1800,
      description: product.description,
      sizes: this.sizes,
    };
  }

  orderNow() {
    const grandTotal = this.getGrandTotal();
    if (grandTotal.quantity === 0) {
      alert('Please select at least one size and color before ordering.');
      return;
    }

    let orderDetails = `Hello, I would like to order the following product:\n- Product: ${this.productItem?.name}\n\n`;

    Object.entries(this.colorSizeSelections).forEach(([color, selections]) => {
      if (selections.length > 0) {
        const colorTotal = selections.reduce(
          (sum, item) => sum + item.quantity,
          0,
        );
        const sizesText = selections
          .map((s) => `${s.size} (Qty: ${s.quantity})`)
          .join(', ');

        orderDetails += `Color: ${color}\n`;
        orderDetails += `Sizes: ${sizesText}\n`;
        orderDetails += `Color Total: ${colorTotal} pieces\n\n`;
      }
    });

    orderDetails += `Total Quantity: ${grandTotal.quantity}\n`;
    orderDetails += `Unit Price: ${this.convertedPrice.toFixed(2)} ${this.getCurrencySymbol()}\n`;
    orderDetails += `Total Price: ${grandTotal.price.toFixed(2)} ${this.getCurrencySymbol()}\n\n`;
    orderDetails += `Product Image: ${this.selectedImage?.imageUrl}`;

    // Encode details for mailto
    const subject = `Order Request - ${this.productItem?.name}`;
    const body = encodeURIComponent(orderDetails);
    const email = 'wemblesport@gmail.com';

    // Open default mail client
    window.location.href = `mailto:${email}?subject=${encodeURIComponent(subject)}&body=${body}`;
  }

  addToCart(): void {
    if (
      !this.productItem ||
      !this.selectedImage ||
      this.selectedSizes.length === 0
    ) {
      return;
    }

    const productId = this.route.snapshot.paramMap.get('id');
    if (!productId) {
      return;
    }

    this.selectedSizes.forEach((selection) => {
      const variant = `${this.selectedColor} / ${selection.size}`;
      const cartItem: CartItem = {
        id: `${productId}-${this.selectedColor}-${selection.size}`,
        title: this.productItem.name,
        imageUrl: this.selectedImage.imageUrl,
        price: this.convertedPrice,
        variant,
        quantity: selection.quantity,
      };
      this.cartService.addItem(cartItem);
    });

    const lang = localStorage.getItem('lang') || 'en';
    this.router.navigateByUrl(`/${lang}/cart`);
  }

  goBackToProducts(): void {
    const lang = localStorage.getItem('lang') || 'en';
    this.router.navigateByUrl(`/${lang}/products`);
  }

  toggleWishlist(event?: Event): void {
    event?.stopPropagation();
    const id = this.route.snapshot.paramMap.get('id');
    if (!id) {
      return;
    }

    const ids = this.readWishlistIds();
    const exists = ids.includes(id);
    const next = exists ? ids.filter((x) => x !== id) : [...ids, id];
    localStorage.setItem(this.wishlistKey, JSON.stringify(next));
    this.isWishlisted = !exists;
  }

  shareProduct(event?: Event): void {
    event?.stopPropagation();
    const shareUrl = window.location.href;
    const shareData = {
      title: this.productItem?.name || 'Product',
      text: this.productItem?.description || '',
      url: shareUrl,
    };

    if (navigator.share) {
      navigator.share(shareData).catch(() => {});
      return;
    }

    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(shareUrl).catch(() => {});
    }
  }

  private readWishlistIds(): string[] {
    if (typeof window === 'undefined') {
      return [];
    }
    const raw = localStorage.getItem(this.wishlistKey);
    if (!raw) {
      return [];
    }
    try {
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }

  get isRTL(): boolean {
    return document.documentElement.dir === 'rtl';
  }
}
